import Vue from 'vue';

import { debounce, whenElementIsVisible } from '@dui/helpers/util-funcs';
import { DEBOUNCE_TIME } from '@d-shared/config';
import duiPluralize from '@dui/filters/dui-pluralize/dui-pluralize';
import followersService from '@d-services/followers-service/followers-service';
import { getErrorMessage } from '@d-shared/utils/errors';

export default {
  props: {
    isSelf: {
      type: Boolean,
      default: false,
    },
    type: {
      required: true,
      validator: function(value) {
        return [
          'followers',
          'follows',
        ].indexOf(value) !== -1;
      },
    },
    userName: {
      type: String,
      default: null,
    },
    userId: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    followersMixinData: {
      disableLoadMore: null,
      followsType: null,
      isEmpty: false,
      isLoading: false,
      loadMoreRefElement: null,
      paginationInfo: {
        hasMoreResults: false,
        pageNum: null,
        perPage: null,
        previousSearch: null,
      },
      results: {
        meta: {
          total_count: 0,
        },
        usersList: [],
      },
      searchConfig: null,
      searchText: null,
      titleText: null,
    },
  }),

  computed: {
    noResults() {
      return !!this.followersMixinData.searchText && (this.followersMixinData.results.meta.total_count === 0);
    },
  },

  beforeDestroy() {
    if (this.followersMixinData.disableLoadMore) {
      this.followersMixinData.disableLoadMore();
    }
  },

  methods: {
    clearResults() {
      this.resetPagination();
      this.$set(this.followersMixinData.results, 'meta', null);
      this.$set(this.followersMixinData.results, 'usersList', []);
      this.$set(this.followersMixinData, 'searchText', null);
    },

    enableLoadMore() {
      if (this.followersMixinData.disableLoadMore) {
        this.followersMixinData.disableLoadMore();
      }

      const disableLoadMore = whenElementIsVisible('#load-more-follows', this.loadMore.bind(this), this.followersMixinData.loadMoreRefElement);
      this.$set(this.followersMixinData, 'disableLoadMore', disableLoadMore);
    },

    getTitleText() {
      if (this.followersMixinData.searchText) {
        if (!this.followersMixinData.results.meta.total_count) {
          return 'No results';
        }

        return `Found ${ this.followersMixinData.results.meta.total_count } ${ duiPluralize(this.followersMixinData.results.meta.total_count, 'result', 'results') }`;
      }

      const userName = this.userName || 'User';

      if (!this.followersMixinData.results.meta.total_count) {
        return [
          this.isSelf ? `You are` : `${ userName } is`,
          'not',
          (this.type === 'followers') ? 'followed by' : 'following',
          'anyone yet',
        ].join(' ');
      }

      return [
        this.isSelf ? `You are` : `${ userName } is`,
        (this.type === 'followers') ? 'followed by' : 'following',
        this.followersMixinData.results.meta.total_count.toLocaleString(),
        duiPluralize(this.followersMixinData.results.meta.total_count, 'person', 'people'),
      ].join(' ');
    },

    loadMore() {
      if (!this.followersMixinData.paginationInfo.hasMoreResults ||
        this.followersMixinData.isLoading) {
        return;
      }

      this.performSearch();
    },

    async performSearch() {
      if (this.followersMixinData.isLoading) {
        return;
      }

      this.$set(this.followersMixinData, 'isLoading', true);
      this.updatePaginationAndResults();

      try {
        let results;

        switch (this.followersMixinData.followsType) {
          case 'suggestions':
            results = await followersService.getSuggestions(this.followersMixinData.searchText, this.followersMixinData.paginationInfo.pageNum, this.followersMixinData.paginationInfo.perPage);
            break;
          case 'followers':
            results = await followersService.getFollowers(this.userId, this.followersMixinData.searchText, this.followersMixinData.paginationInfo.pageNum, this.followersMixinData.paginationInfo.perPage);
            break;
          default:
            results = await followersService.getFollows(this.userId, this.followersMixinData.searchText, this.followersMixinData.paginationInfo.pageNum, this.followersMixinData.paginationInfo.perPage);
            break;
        }

        this.$set(this.followersMixinData.results, 'usersList', this.followersMixinData.results.usersList.concat(results.usersList));
        this.$set(this.followersMixinData.results, 'meta', results.meta);
        this.$set(this.followersMixinData.paginationInfo, 'hasMoreResults', !!results.meta.next_page);

        this.updateEmptyState();
        this.updateTitle();

        if (this.followersMixinData.paginationInfo.hasMoreResults) {
          this.enableLoadMore();
        }
      } catch (error) {
        Vue.duiNotificationManager.error(getErrorMessage(error));
      }

      this.$set(this.followersMixinData, 'isLoading', false);
    },

    removeUser(user) {
      if (this.followersMixinData.followsType === 'suggestions') {
        return;
      }

      const updatedUsersList = this.followersMixinData.results.usersList.filter(u => u.id !== user.id);
      this.$set(this.followersMixinData.results, 'usersList', updatedUsersList);
      this.$set(this.followersMixinData.results.meta, 'total_count', (this.followersMixinData.results.meta.total_count - 1));
      this.updateTitle();
    },

    resetPagination() {
      this.$set(this.followersMixinData.paginationInfo, 'pageNum', null);
      this.$set(this.followersMixinData.paginationInfo, 'perPage', this.followersMixinData.searchConfig.initialSearch);
    },

    setupFollowersListMixin(followsType, searchConfig, loadMoreRefElement = window) {
      this.$set(this.followersMixinData, 'followsType', followsType);
      this.$set(this.followersMixinData, 'loadMoreRefElement', loadMoreRefElement);
      this.$set(this.followersMixinData, 'searchConfig', searchConfig);
      this.$set(this.followersMixinData.paginationInfo, 'perPage', this.followersMixinData.searchConfig.initialSearch);
    },

    textSearchFollows: debounce(function() {
      this.performSearch();
    }, DEBOUNCE_TIME.normal),

    updateEmptyState() {
      this.$set(this.followersMixinData, 'isEmpty', (!this.followersMixinData.searchText && !this.followersMixinData.results.meta.total_count));
    },

    updatePaginationAndResults() {
      if (this.followersMixinData.results.usersList.length && this.followersMixinData.searchText === this.followersMixinData.paginationInfo.previousSearch) {
        this.followersMixinData.paginationInfo.pageNum = Math.floor(this.followersMixinData.results.usersList.length / this.followersMixinData.searchConfig.moreResults) + 1;
        this.followersMixinData.paginationInfo.perPage = this.followersMixinData.searchConfig.moreResults;
        return;
      }

      this.followersMixinData.paginationInfo.pageNum = null;
      this.followersMixinData.paginationInfo.previousSearch = this.followersMixinData.searchText;
      this.followersMixinData.paginationInfo.perPage = this.followersMixinData.searchConfig.initialSearch;
      this.$set(this.followersMixinData.results, 'usersList', []);
    },

    updateTitle() {
      const titleText = this.getTitleText();
      this.$set(this.followersMixinData, 'titleText', titleText);
    },
  },
};
